/** Root component: providers + router. */
import { QueryCache, QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { RouterProvider } from "@tanstack/react-router";
import { type ReactElement, useEffect, useMemo } from "react";

import { AuthProvider, useAuth } from "@/app/auth";
import { createAppRouter } from "@/app/router";
import { ApiError } from "@/api/problem";
import { captureException, setTelemetryUser } from "@/shared/lib/telemetry";

/** Create the shared query client with sane defaults for an ops dashboard. */
export function createQueryClient(): QueryClient {
  return new QueryClient({
    queryCache: new QueryCache({
      onError: (error, query) => {
        captureException(error, { queryKey: query.queryKey });
      },
    }),
    defaultOptions: {
      queries: {
        staleTime: 15_000,
        refetchOnWindowFocus: true,
        retry: (count, error) => {
          // Don't hammer the API on auth/permission/validation failures.
          if (error instanceof ApiError && error.status > 0 && error.status < 500) {
            return false;
          }
          return count < 2;
        },
      },
    },
  });
}

/** Mounts the router once auth context exists. */
function RouterWithAuth({ router }: { router: ReturnType<typeof createAppRouter> }): ReactElement {
  const auth = useAuth();
  // Re-run route guards whenever the auth state flips (login/logout/boot).
  useEffect(() => {
    void router.invalidate();
  }, [router, auth.status]);
  useEffect(() => {
    setTelemetryUser(auth.me ? { id: auth.me.user_id, tenant: auth.me.active_tenant.slug } : null);
  }, [auth.me]);
  return <RouterProvider router={router} context={{ auth }} />;
}

/** Application root. */
export function App({
  queryClient,
  router,
}: {
  queryClient?: QueryClient;
  router?: ReturnType<typeof createAppRouter>;
}): ReactElement {
  const qc = useMemo(() => queryClient ?? createQueryClient(), [queryClient]);
  const r = useMemo(() => router ?? createAppRouter(), [router]);
  return (
    <QueryClientProvider client={qc}>
      <AuthProvider>
        <RouterWithAuth router={r} />
      </AuthProvider>
    </QueryClientProvider>
  );
}
